import { useLevelGeometry } from '@hooks'
import { useEffect, useMemo } from 'react'
import { type DataTexture } from 'three'

import { bakeTerrainSurface } from '@utils'

export interface TerrainMask {
  groundTexture: DataTexture
  maskTexture: DataTexture
  sampleBrown: (wx: number, wz: number) => boolean
  sampleDarkGreen: (wx: number, wz: number) => boolean
  sampleTrackDist: (wx: number, wz: number) => number
  /** Bilinear sample of mask R (green) and G (density) in [0, 1], matching GPU LinearFilter. */
  sampleGrass: (wx: number, wz: number) => { green: number; density: number }
  side: number
  centerOffset: number
  radiusFactor: number
}

const CURVE_SAMPLES = 4

export function useTerrainMask(side: number, seed: number, radiusFactor = 1): TerrainMask {
  const { size, cellGeometries } = useLevelGeometry()

  const result = useMemo(() => {
    const rawPoints: number[] = []

    for (const { position, curves } of cellGeometries.values()) {
      for (const curve of curves) {
        for (let i = 0; i <= CURVE_SAMPLES; i++) {
          const p = curve.getPoint(i / CURVE_SAMPLES)
          rawPoints.push(position.x + p.x, position.z + p.z)
        }
      }
    }

    const { groundTexture, maskTexture, trackDist, textureSize } = bakeTerrainSurface(
      rawPoints,
      side,
      size.centerOffset,
      seed,
      radiusFactor,
    )

    const pixels = maskTexture.image.data as Uint8Array

    const pixelIdx = (wx: number, wz: number): number => {
      const u = (wx - size.centerOffset) / side + 0.5
      const v = 0.5 - wz / side
      if (u < 0 || u > 1 || v < 0 || v > 1) return -1
      const col = Math.max(0, Math.min(textureSize - 1, Math.round(u * textureSize - 0.5)))
      const row = Math.max(0, Math.min(textureSize - 1, Math.round(v * textureSize - 0.5)))
      return row * textureSize + col
    }

    const sampleBrown = (wx: number, wz: number): boolean => {
      const idx = pixelIdx(wx, wz)
      return idx >= 0 && pixels[idx * 4 + 2] > 128
    }

    const sampleDarkGreen = (wx: number, wz: number): boolean => {
      const idx = pixelIdx(wx, wz)
      if (idx < 0) return false
      return pixels[idx * 4] > 128 && pixels[idx * 4 + 1] > 180
    }

    const sampleTrackDist = (wx: number, wz: number): number => {
      const idx = pixelIdx(wx, wz)
      return idx >= 0 ? trackDist[idx] : 0
    }

    const sampleGrass = (wx: number, wz: number) => {
      const max = textureSize - 1
      const x = Math.max(
        0,
        Math.min(max, ((wx - size.centerOffset) / side + 0.5) * textureSize - 0.5),
      )
      const y = Math.max(0, Math.min(max, (0.5 - wz / side) * textureSize - 0.5))
      const x0 = Math.floor(x)
      const y0 = Math.floor(y)
      const x1 = Math.min(max, x0 + 1)
      const y1 = Math.min(max, y0 + 1)
      const fx = x - x0
      const fy = y - y0

      const bilinear = (channel: number) => {
        const p = (col: number, row: number) => pixels[(row * textureSize + col) * 4 + channel]
        const top = p(x0, y0) * (1 - fx) + p(x1, y0) * fx
        const bottom = p(x0, y1) * (1 - fx) + p(x1, y1) * fx
        return (top * (1 - fy) + bottom * fy) / 255
      }

      return { green: bilinear(0), density: bilinear(1) }
    }

    return {
      groundTexture,
      maskTexture,
      sampleBrown,
      sampleDarkGreen,
      sampleTrackDist,
      sampleGrass,
      side,
      centerOffset: size.centerOffset,
      radiusFactor,
    }
  }, [size, cellGeometries, side, seed, radiusFactor])

  useEffect(() => {
    return () => {
      result.groundTexture.dispose()
      result.maskTexture.dispose()
    }
  }, [result])

  return result
}
